import React, { useEffect, useState } from "react";
import { useApp } from "@/contexts/AppContext";
import { api, formatError } from "@/lib/api";
import { runAIPrompt } from "@/lib/ai";
import { PageHeader } from "@/components/common/Bits";
import { Save, Shield, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAIOutputs } from "@/hooks/useAIOutputs";
import AICategoryPanel from "@/components/ai/AICategoryPanel";

const SECTIONS = [
  ["core_theory", "Κεντρική Υπερασπιστική Γραμμή"],
  ["alternative_explanations", "Εναλλακτικές ερμηνείες"],
  ["facts_supporting", "Γεγονότα που στηρίζουν την υπεράσπιση"],
  ["facts_against", "Γεγονότα που βλάπτουν την υπεράσπιση"],
  ["prosecution_weaknesses", "Αδυναμίες κατηγορούσας αρχής"],
  ["missing_evidence", "Ελλιπή αποδεικτικά"],
  ["credibility_attacks", "Επιθέσεις στην αξιοπιστία"],
  ["procedural_objections", "Δικονομικές ενστάσεις"],
  ["trial_themes", "Θέματα δίκης"],
  ["closing_themes", "Θέματα τελικών αγορεύσεων"],
  ["appeal_points", "Λόγοι έφεσης"],
];

export default function DefenseTheory() {
  const { t, currentCase } = useApp();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const { outputs: aiOutputs, addOutput: addAIOutput } = useAIOutputs(currentCase?.id);

  useEffect(() => {
    if (!currentCase) return;
    api.get(`/cases/${currentCase.id}/defense-theory`).then(r => setData(r.data)).catch(()=>{});
  }, [currentCase]);

  if (!data) return null;

  const save = async () => {
    setSaving(true);
    try {
      const { data: r } = await api.put(`/cases/${currentCase.id}/defense-theory`, data);
      setData(r);
      toast.success(t("saved"));
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  const generateAI = async () => {
    setAiLoading(true);
    try {
      const result = await runAIPrompt({ prompt_key: "defense_theory", case_id: currentCase.id, model: "claude" });
      setData(d => ({ ...d, core_theory: (d.core_theory ? d.core_theory + "\n\n" : "") + "[AI suggestion]\n" + result.output }));
      toast.success("AI πρόταση προστέθηκε");
    } catch (e) { toast.error(e.message || "Σφάλμα AI"); }
    finally { setAiLoading(false); }
  };

  return (
    <div data-testid="defense-theory-page">
      <PageHeader
        title="Υπερασπιστική Γραμμή"
        subtitle="Καρδιά της στρατηγικής υπεράσπισης — θεωρία, αδυναμίες κατηγορίας, στρατηγικά θέματα."
        action={
          <div className="flex gap-2">
            <button data-testid="dt-ai-btn" onClick={generateAI} disabled={aiLoading}
              className="flex items-center gap-1.5 px-4 py-2 rounded-md border gold-border gold-text font-medium text-[13px] hover:bg-[hsl(var(--gold)/0.1)] disabled:opacity-60">
              <Sparkles className="size-4"/>{aiLoading ? "AI..." : "Πρόταση AI"}
            </button>
            <button data-testid="dt-save-btn" onClick={save} disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-[hsl(var(--gold))] text-[hsl(var(--background))] font-medium text-[13px] disabled:opacity-60">
              <Save className="size-4"/>{saving ? t("loading") : t("save")}
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {SECTIONS.map(([k, label]) => (
          <div key={k} className="surface-card surface-card-hover p-5" data-testid={`dt-section-${k}`}>
            <div className="flex items-center gap-2 mb-2">
              <Shield className="size-4 gold-text" strokeWidth={1.5}/>
              <h3 className="font-heading font-semibold text-[13.5px]">{label}</h3>
            </div>
            <textarea
              data-testid={`dt-field-${k}`}
              value={data[k] || ""} onChange={e => setData({ ...data, [k]: e.target.value })}
              rows={5}
              className="w-full bg-muted/30 border border-border/60 rounded-md px-3 py-2 text-[13px] outline-none focus:border-[hsl(var(--gold)/0.5)] resize-none leading-relaxed"
            />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <AICategoryPanel
          caseId={currentCase?.id} promptKey="defense_theory" titleEl="Υπερασπιστική Γραμμή — Πλήρες Έγγραφο (AI)"
          outputs={aiOutputs} onGenerated={addAIOutput}
        />
        <AICategoryPanel
          caseId={currentCase?.id} promptKey="devils_advocate" titleEl="Devil's Advocate — Οπτική Κατηγορίας (AI)"
          outputs={aiOutputs} onGenerated={addAIOutput}
        />
      </div>
    </div>
  );
}
